// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var m = require('mobx');
  var dataFetcher = require('fetchers/Izenda.ConnectionStringDataFetcher');
  var uiState = require('logic/Izenda.UiState.js');
  var constants = require('logic/data/Izenda.ConnectionConstants');

  var Connections = require('./models/Izenda.Connections');

  const { filter, isEmpty, isNil, create, head, escapeRegExp, findIndex, find } = require('lodash/fp');

  function ConnectionStore() {
    var observable = {
      // A filtered connection list that matches the filter string
      filteredConnections: function() {
        var filterRegex = new RegExp(escapeRegExp(this.filter), 'i');
        var match = function(item) {
          return filterRegex.test(item.name);
        };
        return filter(match, this.list);
      },
      tenantId: function() {
        return uiState.getTenantId();
      }
    };

    Connections.call(observable);

    // Extend the store from connections model and make it observable
    m.extendObservable(this, observable);

    this.dispose = m.observe(
      this.list,
      function() {
        // Move current connection to next eligible connection in the list
        // whenever the list changed.
        this.nextConnection(this.item ? this.item.id : null);
      }.bind(this)
    );

    // whenever uiState changed the tenant.
    this.disposeChangeTenant = m.observe(
      uiState,
      'tenantId',
      function(change) {
        this.addConnection();
        this.loadList(change);
      }.bind(this)
    );
  }
  ConnectionStore.prototype = create(Connections.prototype, {
    constructor: ConnectionStore
  });

  /**
   * Move to next eligible connection in showing list.
   */
  ConnectionStore.prototype.nextConnection = m.action(function(startId) {
    if ((isNil(this.item) || isNil(this.item.id)) && isNil(startId)) {
      if (isEmpty(this.list)) {
        // Case of intial loading the empty list, a new connection detail is active
        // in order to allow adding new connection.
        this.addConnection();
      } else {
        // Case of first loading (active item is undefined), move to first connection
        const firstItem = head(this.list);
        this.item = firstItem;
      }
    } else if (this.item.id !== startId) {
      const next = find(['id', startId], this.filteredConnections) || find(['id', startId], this.list);
      if (isNil(next)) {
        this.addConnection();
      } else {
        this.item = next;
      }
    } else if (this.tenantId) {
      // In case of when change tenant then get the first value of list connection string
      const firstItem = head(this.list);
      isNil(firstItem) ? this.addConnection() : (this.item = firstItem);
    }
  });

  /**
   * Load all connections and supported db types from API
   * @return {Promise} A promise of loading connection list
   */
  ConnectionStore.prototype.loadList = m.action(function(tenantId) {
    var store = this;
    var tenant = isEmpty(tenantId) ? this.tenantId : tenantId;
    return Promise.join(
      dataFetcher.getAllConnectionString(tenant),
      dataFetcher.getSupportedDatabaseType(),
      m.action(function(connectionList, dbTypeList) {
        // Defer notifying changes until reading finished
        store.readDbTypeList(dbTypeList);
        store.readConnectionList(connectionList);
        return null;
      })
    ).then(function() {
      if (isEmpty(store.list)) {
        store.addConnection();
      }
    });
  });

  /**
   * Update toggled visibility of connection item
   * @param  {connectionDetail} item
   */
  ConnectionStore.prototype.updateToggleVisibility = m.action(function(item) {
    var updateVisibility = item.visible ? dataFetcher.invisible : dataFetcher.visible;

    return updateVisibility(item.id).then(
      m.action(function() {
        uiState.increaseDetectChangeVisibleConnection();
        item.toggleVisibility();
        return Promise.resolve();
      })
    );
  });

  /**
   * Delete a connection. Side effect of deletion is moving selected item
   * in the list to next one.
   * @param  {Object} delItem Deleted connection
   * @param  {Promise} A promise of deletion
   */
  ConnectionStore.prototype.deleteConnection = m.action(function(delItem) {
    // Find next available connection in the list by round-robin ordering pattern
    var findNextId = function(availableList, fullList, current) {
      function nextEligible(list) {
        if (delItem.id === current.id && list.length > 1) {
          var index = findIndex(['id', delItem.id], list);
          // If deleted item is the last item, then take first one, but the next one.
          return index === list.length - 1 ? list[0].id : list[index + 1].id;
        } else if (delItem.id !== current.id) {
          return current.id;
        }
        return null;
      }

      return nextEligible(availableList) || nextEligible(fullList);
    };

    return dataFetcher
      .delete(delItem.id)
      .then(
        function() {
          var store = this;
          m.transaction(function() {
            uiState.increaseDetectChangeVisibleConnection();
            // Select next item in filtered connection list
            var nextId = findNextId(store.filteredConnections, store.list, store.item);
            store.nextConnection(nextId);

            // Delete connection detail instance in the list
            var idx = findIndex(['id', delItem.id], store.list);
            store.list.splice(idx, 1);
          });
          return null;
        }.bind(this)
      )
      .then(function() {
        // only notify ContextMenu if this connection has changes
        if (delItem.change !== constants.DETECTED_CHANGE_LEVEL.NONE) {
          uiState.increaseDetectChangeCount();
        }
      });
  });

  /**
   * Add a new connection to list.
   * @param {Object} connectionDetail Added connection
   */
  ConnectionStore.prototype.addConnectionToList = m.action(function(connectionDetail) {
    this.list.push(connectionDetail);
  });

  module.exports = ConnectionStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ConnectionStore.js